import React from 'react';
import { ExceptionReference, ExceptionReferenceTypes } from '@/redux/slices/slice_types';
import { LabelValue } from '@/components/_common';
import { TableStatus } from '@/components/_common/Table';
import TitleHierarchy from '@/components/_common/TitleHierarchy';
import { useAppSelector } from '@/redux/hooks';
import { selectJobHumanStatuses } from '@/redux/selectors/constantsSelectors';
import { findandFormatUTCDateInString } from '@/_utils/date';
import TableJobStage from '@/components/_common/Table/TableJobStage';
import { StatusColors } from '@/constants/constants';
import { CircularProgress } from '@mui/material';
import { LabelValueDefaultProps } from '@/components/Exceptions/types';
import { useRefDetailsLoader } from '@/components/Exceptions/References/refDetailsLoader.hook';
import Notes from '@/components/_common/Notes/Notes';

interface Props {
  primaryReference: ExceptionReference;
  labelValueProps: LabelValueDefaultProps;
}

const PrimaryBasedReferences: React.FC<Props> = ({ primaryReference, labelValueProps }) => {
  const pendingAssetHumanStatuses = useAppSelector(selectJobHumanStatuses);

  const { isLoading, details: primaryReferenceDetails } = useRefDetailsLoader(primaryReference);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!primaryReferenceDetails) {
    return;
  }

  if (primaryReferenceDetails.type === ExceptionReferenceTypes.Order) {
    const { order } = primaryReferenceDetails;
    return (
      <>
        <LabelValue label="Status" {...labelValueProps}>
          <TableStatus status={order.status} />
        </LabelValue>
      </>
    );
  } else if (primaryReferenceDetails.type === ExceptionReferenceTypes.Job) {
    const { job } = primaryReferenceDetails;
    return (
      <>
        <LabelValue label="Title" {...labelValueProps}>
          <TitleHierarchy title={job.title} nameFallback={job.description} versionName={job.version.name} />
        </LabelValue>
        {job.version.name && (
          <LabelValue label="Version" {...labelValueProps}>
            {job.version.name}
          </LabelValue>
        )}
        {job.customer && (
          <LabelValue label="Customer" {...labelValueProps}>
            {job.customer}
          </LabelValue>
        )}
        {job.profile?.name && (
          <LabelValue label="Profile" {...labelValueProps}>
            {findandFormatUTCDateInString(job.profile.name)}
          </LabelValue>
        )}
        <LabelValue label="Status" {...labelValueProps}>
          <TableJobStage
            status={job.status === 'cancelled' ? job.status : job.stage || job.status}
            colorOverride={StatusColors[job.status as keyof typeof StatusColors]}
          />
        </LabelValue>
        <LabelValue label="Job notes" {...labelValueProps}>
          <Notes data={job} override type="jobs" />
        </LabelValue>
      </>
    );
  } else if (primaryReferenceDetails.type === ExceptionReferenceTypes.Task) {
    const { task } = primaryReferenceDetails;
    const { status, status_reason, job } = task;

    return (
      <>
        <LabelValue label="Title" {...labelValueProps}>
          <TitleHierarchy title={job.title} versionName={job.version?.name} />
        </LabelValue>
        {job.version?.name && (
          <LabelValue label="Version" {...labelValueProps}>
            {job.version.name}
          </LabelValue>
        )}
        <LabelValue label="Status" {...labelValueProps}>
          <TableStatus status={status} statusReason={status === 'failed' || status === 'in_error' ? status_reason : undefined} />
        </LabelValue>
        <LabelValue label="Task notes" {...labelValueProps}>
          <Notes data={task} override type="jobDetailTasks" />
        </LabelValue>
      </>
    );
  } else if (primaryReferenceDetails.type === ExceptionReferenceTypes.Asset) {
    const { asset } = primaryReferenceDetails;
    const firstJob = asset.jobs.at(0);

    return (
      <>
        {firstJob && (
          <LabelValue label="Title" {...labelValueProps}>
            <TitleHierarchy title={firstJob.title} versionName={firstJob.version?.name} />
          </LabelValue>
        )}
        {firstJob?.version?.name && (
          <LabelValue label="Version" {...labelValueProps}>
            {firstJob.version.name}
          </LabelValue>
        )}
        <LabelValue label="Status" {...labelValueProps}>
          <TableStatus status={asset.status} customStatuses={pendingAssetHumanStatuses} statusReason={asset.status_reason} />
        </LabelValue>
        <LabelValue label="Asset notes" {...labelValueProps}>
          <Notes data={asset} override type="pendingAssets" />
        </LabelValue>
      </>
    );
  } else if (primaryReferenceDetails.type === ExceptionReferenceTypes.Ingest) {
    const { ingestFile } = primaryReferenceDetails;
    const { stage, status, status_reason, title_serialized, version } = ingestFile;

    return (
      <>
        {title_serialized && (
          <LabelValue label="Title" {...labelValueProps}>
            <TitleHierarchy title={title_serialized} versionName={version?.name} />
          </LabelValue>
        )}
        {version?.name && (
          <LabelValue label="Version" {...labelValueProps}>
            {version.name}
          </LabelValue>
        )}
        <LabelValue label="Status" {...labelValueProps}>
          <TableStatus status={stage || status} customStatuses={pendingAssetHumanStatuses} statusReason={status_reason} />
        </LabelValue>
        <LabelValue label="Ingest notes" {...labelValueProps}>
          <Notes data={ingestFile} override type="fileIngest" />
        </LabelValue>
      </>
    );
  }
};

export default PrimaryBasedReferences;
